import React, { useState, useEffect } from 'react';
import { BarChart3, PieChart, TrendingUp, Download, Loader2, Building2, GraduationCap, DollarSign, Calendar } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PlacementReports = () => {
  const [reportType, setReportType] = useState('company_wise');
  const [reportData, setReportData] = useState([]);
  const [placedStudents, setPlacedStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const token = localStorage.getItem('token');

  const reportTypes = [
    { value: 'company_wise', label: 'Company Wise', icon: Building2 },
    { value: 'course_wise', label: 'Course Wise', icon: GraduationCap },
    { value: 'package_wise', label: 'Package Wise', icon: DollarSign },
    { value: 'monthly', label: 'Monthly Trend', icon: Calendar }
  ];

  useEffect(() => {
    fetchReport();
    fetchPlacedStudents();
  }, [reportType, year]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/placement/analytics/reports?report_type=${reportType}&year=${year}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReportData(response.data.data || []);
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to fetch report');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlacedStudents = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/placement/analytics/placed-students?year=${year}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPlacedStudents(response.data || []);
    } catch (error) {
      console.error('Error fetching placed students:', error);
    }
  };

  const formatSalary = (amount) => {
    if (!amount) return 'N/A';
    return amount >= 100000 ? `₹${(amount / 100000).toFixed(1)}L` : `₹${amount.toLocaleString('en-IN')}`;
  };

  const getPackageLabel = (boundary) => {
    if (boundary === 0) return '< 3L';
    if (boundary === 300000) return '3-5L';
    if (boundary === 500000) return '5-8L';
    if (boundary === 800000) return '8-10L';
    if (boundary === 1000000) return '10-15L';
    if (boundary === 1500000) return '> 15L';
    return 'Other';
  };

  const exportToCSV = () => {
    let csvContent = '';
    
    if (reportType === 'company_wise') {
      csvContent = 'Company,Students Placed,Average Package\n';
      reportData.forEach(r => {
        csvContent += `${r._id},${r.students_placed},${formatSalary(r.avg_salary)}\n`;
      });
    } else if (reportType === 'course_wise') {
      csvContent = 'Course,Students Placed,Average Package\n';
      reportData.forEach(r => {
        csvContent += `${r._id},${r.students_placed},${formatSalary(r.avg_salary)}\n`;
      });
    }

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `placement_report_${reportType}_${year}.csv`;
    a.click();
    toast.success('Report exported successfully');
  };

  return (
    <div className="space-y-6" data-testid="placement-reports-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Placement Reports</h1>
          <p className="text-sm text-gray-500 mt-1">Detailed placement analytics and reports</p>
        </div>
        <button
          onClick={exportToCSV}
          className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
        >
          <Download className="h-4 w-4" />
          Export CSV
        </button>
      </div>

      {/* Report Type Selector */}
      <div className="bg-white rounded-xl shadow p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex gap-2">
            {reportTypes.map(type => (
              <button
                key={type.value}
                onClick={() => setReportType(type.value)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  reportType === type.value
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <type.icon className="h-4 w-4" />
                {type.label}
              </button>
            ))}
          </div>
          <div className="ml-auto">
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {[2024, 2025, 2026].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Report Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Chart/Data Section */}
        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-blue-600" />
            {reportTypes.find(r => r.value === reportType)?.label} Report
          </h3>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : reportData.length > 0 ? (
            <div className="space-y-3">
              {reportType === 'package_wise' ? (
                reportData.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="font-medium">{getPackageLabel(item._id)}</span>
                    <div className="flex items-center gap-4">
                      <div className="w-32 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${Math.min((item.count / Math.max(...reportData.map(r => r.count))) * 100, 100)}%` }}
                        />
                      </div>
                      <span className="font-semibold text-blue-600 w-12 text-right">{item.count}</span>
                    </div>
                  </div>
                ))
              ) : reportType === 'monthly' ? (
                reportData.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="font-medium">{item._id}</span>
                    <div className="flex items-center gap-4">
                      <div className="w-32 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-600 h-2 rounded-full" 
                          style={{ width: `${Math.min((item.placements / Math.max(...reportData.map(r => r.placements))) * 100, 100)}%` }}
                        />
                      </div>
                      <span className="font-semibold text-green-600 w-12 text-right">{item.placements}</span>
                    </div>
                  </div>
                ))
              ) : (
                reportData.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <span className="font-medium">{item._id || 'Unknown'}</span>
                      <p className="text-xs text-gray-500">Avg: {formatSalary(item.avg_salary)}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${Math.min((item.students_placed / Math.max(...reportData.map(r => r.students_placed))) * 100, 100)}%` }}
                        />
                      </div>
                      <span className="font-semibold text-blue-600 w-12 text-right">{item.students_placed}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <PieChart className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              No data available
            </div>
          )}
        </div>

        {/* Summary Stats */}
        <div className="space-y-4">
          <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-xl text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100">Total Placed ({year})</p>
                <p className="text-4xl font-bold mt-1">{placedStudents.length}</p>
              </div>
              <TrendingUp className="h-16 w-16 text-green-200" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Top Recruiters</h3>
            {reportType === 'company_wise' && reportData.slice(0, 5).map((company, idx) => (
              <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                <div className="flex items-center gap-3">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                    idx === 1 ? 'bg-gray-100 text-gray-700' :
                    idx === 2 ? 'bg-orange-100 text-orange-700' :
                    'bg-gray-50 text-gray-500'
                  }`}>
                    {idx + 1}
                  </span>
                  <span className="font-medium">{company._id}</span>
                </div>
                <span className="text-green-600 font-semibold">{company.students_placed} hired</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Placed Students List */}
      <div className="bg-white rounded-xl shadow">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold">Placed Students</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Package</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {placedStudents.length > 0 ? (
                placedStudents.slice(0, 20).map(student => (
                  <tr key={student.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-900">{student.student_name}</p>
                      <p className="text-sm text-gray-500">{student.admission_number}</p>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{student.course_name}</td>
                    <td className="px-6 py-4 text-sm text-blue-600 font-medium">{student.company_name}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{student.job_title}</td>
                    <td className="px-6 py-4 text-sm font-semibold text-green-600">
                      {formatSalary(student.offer_salary)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                    No placed students found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PlacementReports;
